import { FC, ReactNode, useCallback } from 'react';
import { Form, useForm } from 'react-final-form';

import {
  ProgressButton,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { BookingObjectAttribute } from 'src/components/BookingObjectAttribute/BookingObjectAttribute';
import { EmployeeInfo } from 'src/components/EmployeeInfo/EmployeeInfo';
import { Section } from 'src/components/Section/Section';
import { unbookParking } from 'src/store/parking/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { ParkingBookingData } from 'src/types';

import { BookingData } from './BookingData';
import { TimeInterval } from './TimeInterval';

export const UnbookActions: FC = () => {
  const { submit } = useForm();

  const unbook = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');
  }, [submit]);

  return (
    <ProgressButton
      variant='contained'
      onClick={unbook}
      fullWidth
      data-testid='unbookParking'
    >
      Отменить бронь
    </ProgressButton>
  );
};

export interface BusyBookingFieldsProps {
  status: ParkingBookingData;
}

export const BusyBookingFields: FC<BusyBookingFieldsProps> = ({ status }) => {
  return (
    <>
      <BookingData guest={!!status.guestName} />
      <Section title='На кого забронировано' testId='parkingBookedForSection'>
        {status.responsibleEmployeeNumber && (
          <EmployeeInfo
            employeeNumber={status.responsibleEmployeeNumber}
            testId='parkingBookedFor'
          />
        )}
        {!!status.guestName && (
          <Typography variant='body3' data-testid='parkingGuest'>
            {status.guestName}
          </Typography>
        )}
        {status.needPass && (
          <BookingObjectAttribute testId='roomWithFlipChart'>
            Требуется пропуск
          </BookingObjectAttribute>
        )}
      </Section>
      {!!status.authorEmployeeNumber &&
        status.responsibleEmployeeNumber !== status.authorEmployeeNumber && (
          <Section title='Кем забронировано' testId='parkingBookedSection'>
            <EmployeeInfo
              employeeNumber={status.authorEmployeeNumber}
              testId='parkingBookedFor'
            />
          </Section>
        )}

      <TimeInterval status={status} />

      {!!status.carModel && !!status.carNumber && (
        <Section title='Данные автомобиля' testId='parkingCarSection'>
          {!!status.carModel && (
            <Typography variant='body3' data-testid='parkingCarModel'>
              {status.carModel}
            </Typography>
          )}
          {!!status.carNumber && (
            <Typography variant='body3' data-testid='parkingCarNumber'>
              {status.carNumber}
            </Typography>
          )}
        </Section>
      )}
    </>
  );
};

export interface BusyBookingProps {
  children?: ReactNode;
}

export const BusyBooking: FC<BusyBookingProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  const unbook = useCallback(async () => {
    return dispatch(unbookParking()).unwrap();
  }, [dispatch]);

  return (
    <Form onSubmit={unbook} initialValues={{}}>
      {() => children}
    </Form>
  );
};
